import { useCallback, useEffect, useState } from 'react';
import { getConcurrentPresence } from 'clients/ChainClient';
import { CHAIN_PRESENCE_MINIMIZED_KEY } from 'utils/ChainSessionUtils';

export type PresenceType = 'impersonator-active' | 'owner-active' | 'none';

export interface UseConcurrentPresencePollingProps {
  isChildOrImpersonating?: boolean;
}

export interface UseConcurrentPresencePollingResult {
  isConcurrent: boolean;
  presence: PresenceType;
  isMinimized: boolean;
  acknowledge: () => void;
  reopen: () => void;
  reset: () => void;
}

function readMinimizedState(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return sessionStorage.getItem(CHAIN_PRESENCE_MINIMIZED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMinimizedState(minimized: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (minimized) {
      sessionStorage.setItem(CHAIN_PRESENCE_MINIMIZED_KEY, '1');
    } else {
      sessionStorage.removeItem(CHAIN_PRESENCE_MINIMIZED_KEY);
    }
  } catch {
    /* ignore storage access error */
  }
}

/**
 * Periodically polls concurrent presence status for chain accounts or impersonated sessions.
 */
export function useConcurrentPresencePolling({
  isChildOrImpersonating = false,
}: UseConcurrentPresencePollingProps = {}): UseConcurrentPresencePollingResult {
  const [isConcurrent, setIsConcurrent] = useState<boolean>(false);
  const [presence, setPresence] = useState<PresenceType>('none');
  const [isMinimized, setIsMinimized] = useState<boolean>(() => readMinimizedState());

  const shouldPoll = Boolean(isChildOrImpersonating);

  const acknowledge = useCallback(() => {
    setIsMinimized(true);
    writeMinimizedState(true);
  }, []);

  const reopen = useCallback(() => {
    setIsMinimized(false);
    writeMinimizedState(false);
  }, []);

  const reset = useCallback(() => {
    setIsConcurrent(false);
    setPresence('none');
    setIsMinimized(false);
    writeMinimizedState(false);
  }, []);

  useEffect(() => {
    if (!shouldPoll) {
      setIsConcurrent(false);
      setPresence('none');
      return;
    }

    let isMounted = true;
    let isStoppedByAuth = false;

    const checkPresence = async () => {
      if (isStoppedByAuth) {
        return;
      }
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      try {
        const res: any = await getConcurrentPresence();
        if (!isMounted) {
          return;
        }

        if (res?.status === 401 || res?.status === 403) {
          isStoppedByAuth = true;
          return;
        }

        if (res && (res.status === 'OK' || res.status === 200) && Array.isArray(res.data)) {
          const data = res.data[0];
          const concurrent = Boolean(data?.isConcurrent);
          const presenceStatus: PresenceType = data?.presence || 'none';

          setIsConcurrent(concurrent);
          setPresence(presenceStatus);

          if (!concurrent) {
            setIsMinimized(false);
            writeMinimizedState(false);
          } else {
            setIsMinimized(readMinimizedState());
          }
        }
      } catch {
        /* ignore network presence error */
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPresence();
      }
    };

    checkPresence();

    const intervalId = setInterval(checkPresence, 30000);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [shouldPoll, reset]);

  return {
    isConcurrent,
    presence,
    isMinimized,
    acknowledge,
    reopen,
    reset,
  };
}

export default useConcurrentPresencePolling;
